/**
 * featureAvailability — disponibilidade de funcionalidade por rota, decidida em RUNTIME (não no build).
 *
 * Por que runtime e não `import.meta.env`: as variáveis `VITE_*` são congeladas dentro do bundle durante `vite build`, então ligar ou desligar um menu exigiria reconstruir e republicar a imagem do portal — exatamente o custo que já mordeu este projeto quando o slug do tenant ficou preso no build. Aqui a lista chega por `/runtime-config.js`, que o nginx do contêiner monta no arranque a partir da variável `AZIM_ENABLED_FEATURES` injetada pelo Helm. Uma única imagem serve todos os ambientes, e religar uma funcionalidade vira `helm upgrade` em vez de um ciclo completo de build e publicação.
 *
 * Fail-closed por construção: a configuração é uma ALLOWLIST. Ausência de configuração, arquivo não servido, valor malformado ou placeholder que o envsubst não substituiu resultam todos em "funcionalidade indisponível" — nunca em exposição acidental de uma tela inacabada ao cliente.
 *
 * Isto NÃO é autorização. Uma funcionalidade indisponível fica escondida de todo mundo, inclusive do operador de plataforma; quem decide o que cada papel pode ver continua sendo o RBAC em `shared/rbac`, e a palavra final é sempre do servidor. As duas camadas são independentes de propósito: uma responde "esta tela já existe?", a outra responde "você pode vê-la?".
 *
 * Funções puras exceto pela leitura do objeto global, isolada em `readRuntimeConfig` e memoizada — este módulo não importa React.
 */

/**
 * Rotas cuja tela ainda não está pronta para o cliente final e que por isso nascem indisponíveis. Retirar um id daqui torna a rota permanentemente disponível; mantê-lo aqui e listá-lo em `AZIM_ENABLED_FEATURES` a libera apenas no ambiente que a configura.
 *
 * O critério é **a tela estar inacabada**, não ser administrativa nem pouco usada — e é exatamente onde já erramos: `platform` entrou aqui e levou junto a gestão de ESTÁGIOS das oportunidades, além de Branding, Unidades de negócio e Segmentação, todas prontas e em uso. Quem controla se uma tela pronta aparece para determinado papel é o RBAC, nunca esta lista. Antes de acrescentar um id, a pergunta é uma só: um cliente que abrir esta URL hoje encontra funcionalidade pela metade?
 */
export const FEATURE_GATED_ROUTES = [
  "conversations",
  "channels",
  "queues",
  "workflows",
] as const;

export type GatedFeature = (typeof FEATURE_GATED_ROUTES)[number];

const GATED = new Set<string>(FEATURE_GATED_ROUTES);

/** Contrato do objeto que `/runtime-config.js` publica no `window`. */
export interface AzimRuntimeConfig {
  /** Ids de `FEATURE_GATED_ROUTES` liberados neste ambiente. */
  readonly enabledFeatures?: unknown;
}

declare global {
  // eslint-disable-next-line no-var
  var __AZIM_RUNTIME_CONFIG__: AzimRuntimeConfig | undefined;
}

/**
 * Normaliza o valor bruto vindo da configuração de runtime para o conjunto de funcionalidades liberadas.
 *
 * Aceita array de strings ou string separada por vírgula (a forma que sai do envsubst). Qualquer id desconhecido é descartado em silêncio: é assim que um `${AZIM_ENABLED_FEATURES}` não substituído — ou um erro de digitação — acaba sendo inerte em vez de liberar algo por acidente.
 */
export function parseEnabledFeatures(raw: unknown): ReadonlySet<GatedFeature> {
  const tokens: string[] = Array.isArray(raw)
    ? raw.filter((v): v is string => typeof v === "string")
    : typeof raw === "string"
      ? raw.split(",")
      : [];

  const enabled = new Set<GatedFeature>();
  for (const token of tokens) {
    const id = token.trim();
    if (GATED.has(id)) enabled.add(id as GatedFeature);
  }
  return enabled;
}

/** Lê o objeto global sem estourar em ambiente sem `window` (SSR, jsdom, teste). */
function readRuntimeConfig(): AzimRuntimeConfig | undefined {
  try {
    return globalThis.__AZIM_RUNTIME_CONFIG__;
  } catch {
    return undefined;
  }
}

let cache: ReadonlySet<GatedFeature> | null = null;

/** Conjunto de funcionalidades liberadas neste ambiente, memoizado por carga de página. */
export function enabledFeatures(): ReadonlySet<GatedFeature> {
  if (cache === null) cache = parseEnabledFeatures(readRuntimeConfig()?.enabledFeatures);
  return cache;
}

/**
 * Descarta a memoização. Existe para teste — a configuração de runtime é fixa durante a vida de uma página real, então nada em produção precisa chamar isto.
 */
export function resetFeatureAvailabilityCache(): void {
  cache = null;
}

/**
 * Responde se a rota está disponível neste ambiente.
 *
 * Rota que não está em `FEATURE_GATED_ROUTES` está sempre disponível — o portão é uma exceção declarada, não o regime geral.
 */
export function isFeatureAvailable(route: string): boolean {
  if (!GATED.has(route)) return true;
  return enabledFeatures().has(route as GatedFeature);
}
